import { useState, useEffect } from "react";
import { NetworkFetch } from "../network/networkFetch";
import { apiRoutes } from "../network/apiRoutes";

interface WifiToken {
  token?: string;
  expiresAt?: number; // timestamp en ms cuando expira el token
  serviceId: string; // El serviceId para el cual fue generado este token
  expireSession?: number; // timestamp en ms cuando expira la sesión de verificación
  codeExpire?: number; // timestamp en ms cuando expira el código de seguridad
}

interface WifiSendCodeLock {
  blockedUntil: number;
}

// Tipos para CWMP Device Params
export interface CwmpParameterValue {
  _object: boolean;
  _writable: boolean;
  _timestamp?: string;
  _type?: string;
  _value?: string | boolean | number;
}

export interface CwmpWlanParams {
  [key: string]: string | boolean | CwmpParameterValue;
}

export interface CwmpInterfaceConfig {
  ip: string;
  subnet: string;
  gateway: string;
  dns1: string;
  dns2: string;
  vlan: number;
}

export interface ConnectedDevice {
  active: boolean;
  name: string;
  ip: string;
  interface: string;
}

export interface CwmpCapabilities {
  [key: string]: boolean;
}

export interface CwmpDeviceParams {
  model: string;
  params: {
    params: CwmpWlanParams;
    interface: {
      [key: string]: CwmpInterfaceConfig;
    };
  };
}

export interface CwmpDeviceParamsResponse {
  success?: boolean;
  tokenInvalid?: boolean;
  message?: string;
  model?: string;
  params?: {
    params: CwmpWlanParams;
    interface: {
      [key: string]: CwmpInterfaceConfig;
    };
    devices?: ConnectedDevice[];
  };
  capabilities?: CwmpCapabilities;
}

interface UseWifiVerificationReturn {
  isVerified: boolean;
  verificationCode: string;
  isLoadingVerification: boolean;
  isLoadingSettings: boolean;
  isLoadingSendCode: boolean;
  tokenExpiresIn: number | null; // segundos restantes
  isTokenExpired: boolean;
  sendCodeBlockRemaining: number;
  setVerificationCode: (code: string) => void;
  initiateSendCode: (serviceId: string, sendWithoutWpp?: boolean, skipLocalCheck?: boolean) => Promise<{ success: boolean; message?: string; expireSession?: number; codeExpire?: number }>;
  sendVerificationCode: (serviceId: string, code: string) => Promise<{ success: boolean; message?: string; token?: string }>;
  executeWifiAction: (
    serviceId: string,
    action: "restart" | "getPassword" | "getStats"
  ) => Promise<{ success: boolean; tokenInvalid?: boolean }>;
  getCwmpDeviceParams: (serviceId: string, token: string) => Promise<CwmpDeviceParamsResponse | null>;
  setCwmpDeviceParams: (serviceId: string, token: string, params: CwmpWlanParams) => Promise<{ success: boolean; tokenInvalid?: boolean; message?: string }>;
  clearVerification: () => void;
  getToken: () => string | null;
  validateServiceId: (serviceId: string) => boolean;
}

const WIFI_TOKEN_STORAGE_KEY_PREFIX = "wifi_";
const CWMP_STORAGE_KEY_PREFIX = "cwmp_";
const WIFI_SEND_CODE_LOCK_PREFIX = "wifi_send_lock_";
const TOKEN_EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutos en ms
const SEND_CODE_LOCK_TIME = 30 * 60 * 1000; // 30 minutos en ms

const getWifiTokenKey = (serviceId: string): string => {
  return `${WIFI_TOKEN_STORAGE_KEY_PREFIX}${serviceId}`;
};

const getCwmpStorageKey = (serviceId: string): string => {
  return `${CWMP_STORAGE_KEY_PREFIX}${serviceId}`;
};

const getSendCodeLockKey = (serviceId: string): string => {
  return `${WIFI_SEND_CODE_LOCK_PREFIX}${serviceId}`;
};

export function useWifiVerification(serviceId: string): UseWifiVerificationReturn {
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoadingVerification, setIsLoadingVerification] = useState(false);
  const [isLoadingSendCode, setIsLoadingSendCode] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [tokenExpiresIn, setTokenExpiresIn] = useState<number | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [storedServiceId, setStoredServiceId] = useState<string | null>(null);
  const [sendCodeBlockExpiresAt, setSendCodeBlockExpiresAt] = useState<number | null>(null);
  const [sendCodeBlockRemaining, setSendCodeBlockRemaining] = useState(0);

  // Cargar token del localStorage al montar
  useEffect(() => {
    const tokenKey = getWifiTokenKey(serviceId);
    const storedData = localStorage.getItem(tokenKey);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData) as WifiToken;
        const now = Date.now();

        // Verificar si el token ha expirado (si existe)
        if (parsedData.expiresAt && parsedData.expiresAt > now && parsedData.token) {
          setToken(parsedData.token);
          setExpiresAt(parsedData.expiresAt);
          setStoredServiceId(parsedData.serviceId);
          setIsVerified(true);
          setIsTokenExpired(false);
        } else {
          // Token expirado, limpiar
          localStorage.removeItem(tokenKey);
          setIsTokenExpired(true);
          setIsVerified(false);
        }
      } catch (error) {
        console.error("Error loading WiFi token:", error);
        localStorage.removeItem(tokenKey);
      }
    }
  }, [serviceId]);

  // Cargar bloqueo de solicitud de código desde localStorage al montar
  useEffect(() => {
    const lockKey = getSendCodeLockKey(serviceId);
    const storedLockData = localStorage.getItem(lockKey);

    if (!storedLockData) {
      setSendCodeBlockExpiresAt(null);
      setSendCodeBlockRemaining(0);
      return;
    }

    try {
      const parsedLockData = JSON.parse(storedLockData) as WifiSendCodeLock;
      const now = Date.now();

      if (parsedLockData?.blockedUntil && parsedLockData.blockedUntil > now) {
        setSendCodeBlockExpiresAt(parsedLockData.blockedUntil);
      } else {
        localStorage.removeItem(lockKey);
        setSendCodeBlockExpiresAt(null);
        setSendCodeBlockRemaining(0);
      }
    } catch (error) {
      console.error("Error loading WiFi code lock:", error);
      localStorage.removeItem(lockKey);
      setSendCodeBlockExpiresAt(null);
      setSendCodeBlockRemaining(0);
    }
  }, [serviceId]);

  // Actualizar el bloqueo de solicitud de código cada segundo
  useEffect(() => {
    if (!sendCodeBlockExpiresAt) {
      setSendCodeBlockRemaining(0);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = sendCodeBlockExpiresAt - now;

      if (remaining <= 0) {
        const lockKey = getSendCodeLockKey(serviceId);
        setSendCodeBlockExpiresAt(null);
        setSendCodeBlockRemaining(0);
        localStorage.removeItem(lockKey);
        clearInterval(interval);
      } else {
        setSendCodeBlockRemaining(Math.ceil(remaining / 1000));
      }
    }, 1000);

    const initialRemaining = Math.max(0, sendCodeBlockExpiresAt - Date.now());
    setSendCodeBlockRemaining(Math.ceil(initialRemaining / 1000));

    return () => clearInterval(interval);
  }, [sendCodeBlockExpiresAt, serviceId]);

  // Actualizar tiempo restante cada segundo
  useEffect(() => {
    if (!expiresAt) {
      setTokenExpiresIn(null);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = expiresAt - now;

      if (remaining <= 0) {
        // Token expirado
        const tokenKey = getWifiTokenKey(serviceId);
        setIsTokenExpired(true);
        setIsVerified(false);
        setToken(null);
        setExpiresAt(null);
        setTokenExpiresIn(null);
        localStorage.removeItem(tokenKey);
        clearInterval(interval);
      } else {
        // Convertir a segundos
        setTokenExpiresIn(Math.floor(remaining / 1000));
        setIsTokenExpired(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Enviar código automáticamente (sin verificación)
  const initiateSendCode = async (serviceId: string, sendWithoutWpp?: boolean, skipLocalCheck?: boolean): Promise<{ success: boolean; message?: string; expireSession?: number; codeExpire?: number }> => {
    try {
      const now = Date.now();
      if (!skipLocalCheck && sendCodeBlockExpiresAt && sendCodeBlockExpiresAt > now) {
        const remainingSeconds = Math.ceil((sendCodeBlockExpiresAt - now) / 1000);
        return {
          success: false,
          message: `Debes esperar ${Math.ceil(remainingSeconds / 60)} minutos para pedir otro código.`,
        };
      }

      setIsLoadingSendCode(true);

      // Llamar al endpoint para enviar el código de seguridad
      const payload: any = { serviceId };
      if (sendWithoutWpp) {
        payload.nowpp = true;
      }
      const sendCodeResponse = await NetworkFetch(apiRoutes.generateSecurityCode, payload);

      // Siempre extraer y persistir los tiempos que retorne el backend
      // (el back puede cambiar los valores de expiración en cualquier momento)
      const freshCodeExpire: number | undefined = sendCodeResponse?.codeExpire;
      const freshExpireSession: number | undefined = sendCodeResponse?.expireSession;

      if (freshCodeExpire || freshExpireSession) {
        const tokenKey = getWifiTokenKey(serviceId);
        // Mezclar con datos existentes para no perder otros campos (ej: token de sesión)
        const existing = localStorage.getItem(tokenKey);
        const existingData: WifiToken = existing ? JSON.parse(existing) : { serviceId };
        const updatedData: WifiToken = {
          ...existingData,
          serviceId,
          ...(freshExpireSession !== undefined && { expireSession: freshExpireSession }),
          ...(freshCodeExpire !== undefined && { codeExpire: freshCodeExpire }),
        };
        localStorage.setItem(tokenKey, JSON.stringify(updatedData));

        if (freshCodeExpire) {
          const lockKey = getSendCodeLockKey(serviceId);
          localStorage.setItem(lockKey, JSON.stringify({ blockedUntil: freshCodeExpire }));
          setSendCodeBlockExpiresAt(freshCodeExpire);
        }
      }

      // Si la respuesta es exitosa, el código fue enviado (o datos frescos en modo skipLocalCheck)
      if (sendCodeResponse?.success || sendCodeResponse?.message) {
        return {
          success: true,
          expireSession: freshExpireSession,
          codeExpire: freshCodeExpire,
        };
      }

      // En modo skipLocalCheck siempre devolvemos los tiempos frescos aunque no sea success
      if (skipLocalCheck) {
        return {
          success: false,
          expireSession: freshExpireSession,
          codeExpire: freshCodeExpire,
        };
      }

      return { success: false };
    } catch (catchError: any) {
      console.error("Error sending security code:", catchError);
      
      let errorMessage = "Error al enviar el código";
      
      try {
        const errorParse = JSON.parse(catchError);
        errorMessage = errorParse.message || errorMessage;
      } catch {
        errorMessage = catchError?.message || errorMessage;
      }
      
      console.error("Final error message:", errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoadingSendCode(false);
    }
  };
  const sendVerificationCode = async (
    serviceId: string,
    code: string
  ): Promise<{ success: boolean; message?: string; token?: string }> => {
    if (!code.trim()) {
      return { success: false, message: "El código no puede estar vacío" };
    }

    try {
      setIsLoadingVerification(true);

      // Verificar el código ingresado por el usuario
      const verifyCodeResponse = await NetworkFetch(
        apiRoutes.verifySecurityCode,
        {
          serviceId,
          code,
        }
      );

      // Verificar si la respuesta indica un error
      if (verifyCodeResponse?.error === true) {
        console.error("Verification failed:", verifyCodeResponse?.message);
        return {
          success: false,
          message: verifyCodeResponse?.message || "Error al verificar el código",
        };
      }

      // El backend debería retornar un token en la respuesta
      // Estructura esperada: { success: true, token: "...", expiresIn: 1800 }
      const receivedToken =
        verifyCodeResponse?.token || verifyCodeResponse?.data?.token;

      if (!receivedToken) {
        console.error("No token received from verification");
        return {
          success: false,
          message: "No se recibió token del servidor",
        };
      }

      // Guardar token en localStorage CON el serviceId Y los datos de seguridad previos
      const tokenKey = getWifiTokenKey(serviceId);
      const existingData = localStorage.getItem(tokenKey);
      const previousSecurityData = existingData ? JSON.parse(existingData) : {};
      
      // Usar expireSession del backend como tiempo de expiración del token
      const newExpiresAt = previousSecurityData.expireSession || (Date.now() + TOKEN_EXPIRATION_TIME);
      
      const tokenData: WifiToken = {
        token: receivedToken,
        expiresAt: newExpiresAt,
        serviceId: serviceId, // Guardar el serviceId con el token
        expireSession: previousSecurityData.expireSession, // Mantener expireSession del backend
        codeExpire: previousSecurityData.codeExpire, // Mantener codeExpire si existe
      };
      localStorage.setItem(tokenKey, JSON.stringify(tokenData));

      // Actualizar estado
      setToken(receivedToken);
      setExpiresAt(newExpiresAt);
      setStoredServiceId(serviceId);
      setIsVerified(true);
      setIsTokenExpired(false);
      setVerificationCode("");

      return { success: true, token: receivedToken };
    } catch (catchError: any) {
      console.error("Error verifying code:", catchError);
      
      let errorMessage = "Error al verificar el código";
      
      try {
        const errorParse = JSON.parse(catchError);
        errorMessage = errorParse.message || errorMessage;
      } catch {
        errorMessage = catchError?.message || errorMessage;
      }
      
      console.error("Final error message:", errorMessage);
      setVerificationCode("");
      return { success: false, message: errorMessage };
    } finally {
      setIsLoadingVerification(false);
    }
  };

  // TODO: Implementar llamadas al backend con el token
  const executeWifiAction = async (
    serviceId: string,
    action: "restart" | "getPassword" | "getStats"
  ): Promise<{ success: boolean; tokenInvalid?: boolean }> => {
    if (!token || isTokenExpired) {
      setIsVerified(false);
      setToken(null);
      setExpiresAt(null);
      return { success: false };
    }

    try {
      setIsLoadingSettings(true);

      // TODO: Agregar endpoints reales cuando estén disponibles

      // Simular delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return { success: true };
    } catch (error: any) {
      console.error("Error in wifi settings:", error);
      
      // Verificar si el error es por token inválido
      try {
        const errorParse = JSON.parse(error);
        if (errorParse.error === true && errorParse.token === false) {
          // Token inválido, limpiar y notificar
          clearVerification();
          return { success: false, tokenInvalid: true };
        }
      } catch {
        // No es JSON, continuar
      }
      
      return { success: false };
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const clearVerification = () => {
    const tokenKey = getWifiTokenKey(serviceId);
    const cwmpKey = getCwmpStorageKey(serviceId);
    setIsVerified(false);
    setToken(null);
    setExpiresAt(null);
    setStoredServiceId(null);
    setVerificationCode("");
    setTokenExpiresIn(null);
    setIsTokenExpired(false);
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(cwmpKey); // Borrar datos CWMP cuando se expira el token
  };

  const validateServiceId = (currentServiceId: string): boolean => {
    // Si no hay token guardado, es válido (usuario no verificado)
    if (!storedServiceId) return true;
    
    // Si el serviceId actual no coincide con el del token, el token es inválido
    if (storedServiceId !== currentServiceId) {
      console.warn(`ServiceId mismatch: stored=${storedServiceId}, current=${currentServiceId}`);
      return false;
    }
    
    return true;
  };

  const getToken = (): string | null => {
    if (isTokenExpired) {
      return null;
    }
    return token;
  };

  // Obtener parámetros del dispositivo CWMP después de verificación
  const getCwmpDeviceParams = async (serviceId: string, verificationToken: string): Promise<CwmpDeviceParamsResponse | null> => {
    if (!verificationToken) {
      console.error("No token available");
      return null;
    }

    try {
      const response = await NetworkFetch(apiRoutes.cwmpGetDeviceParams, {
        serviceId,
        token: verificationToken,
      });

      // Verificar si la respuesta contiene un error con token inválido
      if (response?.error === true && response?.token === false) {
        console.error("Token inválido en CWMP:", response.message);
        clearVerification();
        return { success: false, tokenInvalid: true };
      }
      
      // Verificar si la respuesta contiene un error (dispositivo offline u otro)
      if (response?.error === true) {
        console.error("Error en CWMP:", response.message);
        return { success: false, message: response.message || "Error al obtener parámetros del dispositivo" };
      }
      
      // Guardar en localStorage mientras el token sea válido
      if (response && !response.error) {
        const cwmpKey = getCwmpStorageKey(serviceId);
        localStorage.setItem(cwmpKey, JSON.stringify(response));
      }
      
      return response as CwmpDeviceParamsResponse;
    } catch (error: any) {
      console.error("Error getting CWMP device params:", error);
      
      // Verificar si el error contiene información de token inválido
      try {
        const errorParse = JSON.parse(error);
        if (errorParse.error === true && errorParse.token === false) {
          clearVerification();
          return { success: false, tokenInvalid: true };
        }
        // Retornar el mensaje de error si está disponible
        if (errorParse.error === true && errorParse.message) {
          return { success: false, message: errorParse.message };
        }
      } catch {
        // No es JSON, continuar
      }
      
      return null;
    }
  };

  // Guardar parámetros del dispositivo CWMP
  const setCwmpDeviceParams = async (serviceId: string, verificationToken: string, params: CwmpWlanParams): Promise<{ success: boolean; tokenInvalid?: boolean; message?: string }> => {
    if (!verificationToken) {
      console.error("No token available");
      return { success: false, message: "No token available" };
    }

    try {
      setIsLoadingSettings(true);
      const response = await NetworkFetch(apiRoutes.cwmpSetDeviceParams, {
        serviceId,
        token: verificationToken,
        params,
      });

      // Verificar si la respuesta contiene un error
      if (response?.error === true) {
        console.error("Error en CWMP SET:", response.message);
        
        // Verificar si es un error de token inválido
        if (response?.token === false) {
          clearVerification();
          return { success: false, tokenInvalid: true, message: "Token inválido" };
        }
        
        return { success: false, message: response?.message || "Error desconocido" };
      }
      
      // Si no hay error, asumir éxito y guardar en localStorage
      if (response?.success === true) {
        const cwmpKey = getCwmpStorageKey(serviceId);
        localStorage.setItem(cwmpKey, JSON.stringify(response));
        return { success: true, message: response?.message };
      }
      
      return { success: false, message: "Error desconocido" };
    } catch (error: any) {
      console.error("Error setting CWMP device params:", error);
      
      // Verificar si el error contiene información de token inválido
      try {
        const errorParse = JSON.parse(error);
        if (errorParse.error === true && errorParse.token === false) {
          clearVerification();
          return { success: false, tokenInvalid: true, message: "Token inválido" };
        }
        return { success: false, message: errorParse.message || "Error desconocido" };
      } catch {
        // No es JSON, continuar
      }
      
      return { success: false, message: error.message || "Error desconocido" };
    } finally {
      setIsLoadingSettings(false);
    }
  };

  return {
    isVerified,
    verificationCode,
    isLoadingVerification,
    isLoadingSendCode,
    isLoadingSettings,
    tokenExpiresIn,
    isTokenExpired,
    sendCodeBlockRemaining,
    setVerificationCode,
    initiateSendCode,
    sendVerificationCode,
    executeWifiAction,
    getCwmpDeviceParams,
    setCwmpDeviceParams,
    clearVerification,
    getToken,
    validateServiceId,
  };
}
